"use client";

import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { IconType } from "react-icons";
import {
  FaCircleHalfStroke,
  FaHeart,
  FaImage,
  FaKeyboard,
  FaShieldHalved,
  FaStar,
  FaUniversalAccess,
  FaUsers,
  FaVolumeHigh,
} from "react-icons/fa6";

const ease = [0.17, 0.85, 0.438, 0.99] as const;

interface StepConfig {
  label: string;
  chip: string;
  bg: string;
  fg: string;
  score: number;
  ring: { left: string; top: string; width: string; height: string };
  icon: IconType;
}

const DEMO_STEPS: StepConfig[] = [
  {
    label: "Keyboard",
    chip: "Keyboard focus visible",
    bg: "#EEEAFE",
    fg: "#5B3FD0",
    score: 62,
    ring: { left: "6.5%", top: "62%", width: "33%", height: "18%" },
    icon: FaKeyboard,
  },
  {
    label: "Contrast",
    chip: "Text contrast fixed",
    bg: "#FEF0D9",
    fg: "#A04B06",
    score: 74,
    ring: { left: "6.5%", top: "24.5%", width: "46%", height: "24%" },
    icon: FaCircleHalfStroke,
  },
  {
    label: "Alt text",
    chip: "Image alt text added",
    bg: "#DDF5E9",
    fg: "#137A50",
    score: 88,
    ring: { left: "58.5%", top: "22.5%", width: "35%", height: "49%" },
    icon: FaImage,
  },
  {
    label: "Screen reader",
    chip: "Screen reader friendly",
    bg: "#FCE7F3",
    fg: "#A3195B",
    score: 98,
    ring: { left: "6.5%", top: "62%", width: "33%", height: "18%" },
    icon: FaVolumeHigh,
  },
];

const WHY_CARDS = [
  {
    line1: "Reach More",
    line2: "Customers",
    bg: "#DDF5E9",
    color: "#137A50",
    icon: FaUsers,
  },
  {
    line1: "Better User",
    line2: "Experience",
    bg: "#EEEAFE",
    color: "#5B3FD0",
    icon: FaHeart,
  },
  {
    line1: "Reduce",
    line2: "Legal Risk",
    bg: "#FEF0D9",
    color: "#B45309",
    icon: FaShieldHalved,
  },
  {
    line1: "Show Your Social",
    line2: "Responsibility",
    bg: "#FCE7F3",
    color: "#A3195B",
    icon: FaStar,
  },
];

export default function AdaComplianceWhyMatters() {
  const reduceMotion = useReducedMotion();
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (reduceMotion) return;
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % DEMO_STEPS.length);
    }, 2800);
    return () => clearInterval(interval);
  }, [reduceMotion]);

  const activeStepConfig = DEMO_STEPS[currentStep];
  const ActiveIcon = activeStepConfig.icon;

  const reveal = {
    initial: reduceMotion ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: reduceMotion ? 0 : 0.65, ease },
  };

  return (
    <>
      {/* ============ WHY ADA ACCESSIBILITY MATTERS ============ */}
      <section className="relative overflow-hidden  py-14 sm:py-20 lg:py-24 transition-colors duration-300 dark:bg-[#070b14]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-14 xl:gap-20">
            <motion.div
              {...reveal}
              className="relative mx-auto w-full max-w-[540px] lg:max-w-none"
            >
              <div className="relative flex flex-col items-center justify-center rounded-3xl bg-linear-to-br from-[#DCE6F2] via-[#EEF2F8] to-[#F6EEF8] p-5 pb-7 pt-14 sm:p-9 sm:pb-9 sm:pt-16 shadow-lg dark:from-slate-900 dark:via-indigo-950/40 dark:to-slate-900 dark:border dark:border-slate-800">
                <div
                  aria-hidden="true"
                  className="relative w-full max-w-[460px]"
                >
                  <div className="absolute -top-11 left-0 z-20 sm:-top-13 sm:-left-2">
                    <motion.div
                      key={currentStep}
                      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, ease }}
                      className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-1.5 text-xs font-bold text-indigo-950 shadow-md shadow-indigo-950/10 dark:bg-slate-900 dark:text-white"
                    >
                      <span
                        style={{
                          backgroundColor: activeStepConfig.bg,
                          color: activeStepConfig.fg,
                        }}
                        className="grid size-6 place-items-center rounded-md"
                      >
                        <ActiveIcon size={13} />
                      </span>
                      <span>{activeStepConfig.chip}</span>
                    </motion.div>
                  </div>

                  <div className="absolute -right-2 -top-12 z-20 w-32 rounded-2xl bg-white p-2.5 px-3 text-left shadow-lg shadow-indigo-950/12 sm:-right-4 sm:-top-14 dark:bg-slate-900 dark:border dark:border-slate-800">
                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400">
                      Accessibility score
                    </p>
                    <div className="flex items-baseline gap-1">
                      <span className="font-poppins text-2xl font-extrabold text-indigo-950 dark:text-white">
                        {activeStepConfig.score}
                      </span>
                      <span className="text-xs text-slate-400">/ 100</span>
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      <motion.div
                        className="h-full rounded-full bg-linear-to-r from-[#F7845A] to-[#E11D63]"
                        animate={{ width: `${activeStepConfig.score}%` }}
                        transition={{ duration: 0.6, ease }}
                      />
                    </div>
                  </div>

                  <div className="relative rounded-t-2xl bg-indigo-950 p-2.5 pb-3 shadow-2xl shadow-indigo-950/30 sm:rounded-t-3xl dark:bg-black">
                    <div className="relative aspect-16/10 w-full overflow-hidden rounded-md bg-white dark:bg-slate-900">
                      <div className="flex h-[11%] items-center justify-between border-b border-slate-100 px-4 dark:border-slate-800">
                        <span className="h-1.5 w-4 rounded-full bg-indigo-950 dark:bg-white" />
                        <div className="flex gap-1.5">
                          <span className="h-1 w-3.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                          <span className="h-1 w-3.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                          <span className="h-1 w-3.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                        </div>
                      </div>

                      <div
                        style={{
                          color: currentStep >= 1 ? "#1E1B4B" : "#CDD2DE",
                        }}
                        className="absolute left-[8%] top-[27%] font-poppins text-[13px] font-bold leading-tight transition-colors duration-500 sm:text-base dark:!text-white"
                      >
                        Shop Fresh,
                        <br />
                        Live Better
                      </div>

                      <span className="absolute left-[8%] top-[53%] h-1 w-[40%] rounded-full bg-slate-200 dark:bg-slate-700" />
                      <span className="absolute left-[8%] top-[58%] h-1 w-[30%] rounded-full bg-slate-200 dark:bg-slate-700" />

                      <div className="absolute left-[8%] top-[64%] flex h-[14%] w-[30%] items-center justify-center rounded-md bg-linear-to-r from-[#F7845A] to-[#E11D63] text-[8.5px] font-bold text-white shadow-sm sm:text-[10px]">
                        Get Free Scan
                      </div>

                      <div className="absolute left-[60%] top-[24%] flex h-[46%] w-[32%] items-center justify-center rounded-lg bg-linear-to-br from-[#7B3FE4] to-[#3B5BDB] text-white/80">
                        <FaImage size={24} />
                      </div>

                      <motion.span
                        initial={false}
                        animate={{
                          opacity: currentStep === 2 ? 1 : 0,
                          y: currentStep === 2 ? 0 : 6,
                        }}
                        transition={{ duration: 0.4 }}
                        className="pointer-events-none absolute left-[54%] top-[74%] rounded-md bg-[#137A50] px-2 py-0.5 font-mono text-[8px] text-white shadow-md sm:text-[9.5px]"
                      >
                        alt=&quot;Fresh fruit basket&quot;
                      </motion.span>

                      <motion.div
                        initial={false}
                        animate={{
                          opacity: currentStep === 3 ? 1 : 0,
                          y: currentStep === 3 ? 0 : 6,
                        }}
                        transition={{ duration: 0.4 }}
                        className="pointer-events-none absolute bottom-[6%] right-[4%] z-10 flex items-center gap-2 rounded-xl rounded-br-xs bg-indigo-950 px-2.5 py-1.5 text-[8.5px] font-bold text-white shadow-lg sm:text-[10px] dark:bg-pink-900"
                      >
                        <span className="flex items-center gap-0.5">
                          <span className="inline-block h-3 w-0.5 animate-pulse rounded-full bg-white" />
                          <span
                            className="inline-block h-3 w-0.5 animate-pulse rounded-full bg-white"
                            style={{ animationDelay: "0.15s" }}
                          />
                          <span
                            className="inline-block h-3 w-0.5 animate-pulse rounded-full bg-white"
                            style={{ animationDelay: "0.3s" }}
                          />
                        </span>
                        <span>&ldquo;Get Free Scan&rdquo;, button</span>
                      </motion.div>

                      <motion.span
                        layout
                        transition={{
                          type: "spring",
                          stiffness: 180,
                          damping: 24,
                        }}
                        style={{
                          left: activeStepConfig.ring.left,
                          top: activeStepConfig.ring.top,
                          width: activeStepConfig.ring.width,
                          height: activeStepConfig.ring.height,
                        }}
                        className="pointer-events-none absolute rounded-lg border-2 border-[#E11D63] shadow-[0_0_0_4px_rgba(225,29,99,0.2)]"
                      />
                    </div>
                  </div>

                  <div className="-ml-[6%] h-3 w-[112%] rounded-b-xl bg-linear-to-b from-[#D5DAE6] to-[#B9C0D0] dark:from-slate-700 dark:to-slate-800" />

                  <div className="absolute -bottom-5 -left-5 z-20 size-14 text-[#E11D63]">
                    <span className="absolute inset-0 animate-ping rounded-full bg-pink-500/20" />
                    <span className="absolute inset-0 rounded-full bg-linear-to-br from-[#F7845A] to-[#E11D63] shadow-lg shadow-pink-500/40 grid place-items-center text-white">
                      <FaUniversalAccess size={26} />
                    </span>
                  </div>
                </div>

                <div
                  role="group"
                  aria-label="Accessibility fix interactive demo"
                  className="mt-6 sm:mt-7 flex flex-wrap items-center justify-center gap-2"
                >
                  {DEMO_STEPS.map((step, idx) => {
                    const isActive = idx === currentStep;
                    return (
                      <button
                        key={step.label}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => setCurrentStep(idx)}
                        className={`min-h-9 cursor-pointer rounded-full px-4 py-1.5 text-xs font-bold transition-all ${
                          isActive
                            ? "bg-indigo-950 text-white shadow-md dark:bg-pink-600"
                            : "border border-slate-200 bg-white text-indigo-950 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                        }`}
                      >
                        {step.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            <motion.div
              {...reveal}
              className="flex w-full flex-col items-start gap-4 sm:gap-5"
            >
              <p className="font-poppins text-xs font-bold uppercase tracking-widest text-pink-700 dark:text-pink-400">
                Why ADA Accessibility Matters
              </p>

              <h2 className="font-poppins text-3xl font-bold leading-tight tracking-tight text-indigo-950 sm:text-4xl dark:text-white">
                A More Inclusive Web Is a{" "}
                <span className="relative inline-block italic text-violet-700 dark:text-violet-400">
                  Stronger
                  {/* <svg
                    aria-hidden="true"
                    width="100%"
                    height="12"
                    viewBox="0 0 150 12"
                    preserveAspectRatio="none"
                    className="absolute -bottom-2 left-0 w-full"
                  >
                    <path
                      d="M3 8c30-6 70-7 144-2"
                      fill="none"
                      stroke="#E11D63"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                    />
                  </svg> */}
                </span>{" "}
                Business
              </h2>

              <p className="max-w-xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
                Accessible websites are easier to use, reach more customers, and
                help you avoid potential legal issues. The ADA requires equal
                access for people with disabilities, and an inaccessible website
                can create barriers for millions of users.
              </p>

              <div className="mt-2 grid w-full grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-3">
                {WHY_CARDS.map((card) => {
                  const CardIcon = card.icon;
                  return (
                    <div
                      key={card.line1 + card.line2}
                      className="group flex flex-col items-center justify-center rounded-2xl bg-[#F3F8FC] px-2 py-4 text-center shadow-md shadow-indigo-950/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:px-2.5 sm:py-5 dark:border dark:border-slate-800 dark:bg-slate-900"
                    >
                      <span
                        style={{ backgroundColor: card.bg, color: card.color }}
                        className="grid size-11 place-items-center rounded-full transition-transform duration-300 group-hover:scale-110"
                      >
                        <CardIcon size={19} />
                      </span>
                      <p className="mt-2.5 flex flex-col items-center justify-center font-poppins text-xs font-bold leading-snug text-indigo-950 sm:text-xs dark:text-white">
                        <span className="whitespace-nowrap">{card.line1}</span>
                        <span className="whitespace-nowrap">{card.line2}</span>
                      </p>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
